import type { Contact } from '@hunting-nature/shared-core/interfaces/contact-response.interface';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { SEARCHBAR_VISIBILITY_THRESHOLD } from '@hunting-nature/shared-ui/helper/searchbar.config';
import type { MenuContactsState } from './menu-contacts.reducer';

export const MENU_CONTACTS_FEATURE_KEY = 'menuContacts';

const selectMenuContactsState = createFeatureSelector<MenuContactsState>(MENU_CONTACTS_FEATURE_KEY);

const getMenuContacts = createSelector(selectMenuContactsState, state => state.contacts);

const getMenuContactsCount = createSelector(selectMenuContactsState, state => state.contacts.length);

const showSearchbar = createSelector(getMenuContactsCount, count => count > SEARCHBAR_VISIBILITY_THRESHOLD);

const getMenuContactsLoading = createSelector(selectMenuContactsState, state => state.loading);

const isMenuContactsSkeletonShown = createSelector(selectMenuContactsState, state => state.loading && !state.loaded);

const getMenuContactsSearchTerm = createSelector(selectMenuContactsState, state => state.searchTerm);

const getMenuContactsFiltered = createSelector(selectMenuContactsState, state => {
  const normalizedSearchTerm = state.searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) {
    return state.contacts;
  }

  return state.contacts.filter((contact: Contact) => {
    const name = contact.alias?.toLowerCase() ?? '';
    const email = contact.email?.toLowerCase() ?? '';
    const invitationEmail = contact.invitationEmail?.toLowerCase() ?? '';
    return name.includes(normalizedSearchTerm) || email.includes(normalizedSearchTerm) || invitationEmail.includes(normalizedSearchTerm);
  });
});

const getMenuContactMenuOpen = createSelector(selectMenuContactsState, state => state.contactMenuOpen);

const getMenuSelectedContact = createSelector(selectMenuContactsState, state => state.selectedContact);

const isInviteNewContactResultModalVisible = createSelector(selectMenuContactsState, state => state.inviteNewContactResultModalVisible);
const isInviteNewContactButtonDisabled = createSelector(
  selectMenuContactsState,
  state => state.inviteNewContactPending || state.inviteNewContactResultModalVisible
);

const isResendInvitationLoading = createSelector(selectMenuContactsState, state => state.resendInvitationLoading);

const isResendInvitationSuccessModalOpen = createSelector(selectMenuContactsState, state => state.resendInvitationSuccessModalOpen);

const isDeleteConfirmationOpen = createSelector(selectMenuContactsState, state => state.deleteConfirmationOpen);

const isDeleteSuccessOpen = createSelector(selectMenuContactsState, state => state.deleteSuccessOpen);

export const MenuContactsSelectors = {
  getMenuContacts,
  getMenuContactsCount,
  showSearchbar,
  getMenuContactsLoading,
  isMenuContactsSkeletonShown,
  getMenuContactsSearchTerm,
  getMenuContactsFiltered,
  getMenuContactMenuOpen,
  getMenuSelectedContact,
  isInviteNewContactResultModalVisible,
  isInviteNewContactButtonDisabled,
  isResendInvitationLoading,
  isResendInvitationSuccessModalOpen,
  isDeleteConfirmationOpen,
  isDeleteSuccessOpen,
};
